import { handleActions } from 'redux-actions';
import { createSelector } from 'reselect';
import produce from 'immer';
import moment from 'moment';
import serializeSections from '../../Utils/sectionsSerializer';

// Helper function to process assessment data
const processAssessmentData = (draft, payload) => {
  draft.id = payload.id;
  if (draft.problems.length === 0) {
    draft.problems = [];
    payload.problems.forEach(p => {
      if (p.lcs) {
        p.attempted = true;
      } else {
        p.attempted = false;
      }
      p.type = 'Coding';
      draft.problems.push(p);
    });
  }
  draft.startTime = payload.start_time;
  draft.duration = payload.duration;
  draft.test_title = payload.test_title;
  draft.remainingTime = payload.remaining_time;
  draft.mcqIds = payload.mcqs;
  const { sections: serializedSections } = serializeSections(payload.sections);
  draft.switchableSections = payload.remaining_time > 0 && serializedSections.length;
  draft.sections = serializedSections;
  draft.sectionProblems = {};
  draft.sections.forEach(section => {
    draft.sectionProblems[section.id] = draft.problems.filter(
      p => section.problems && section.problems.includes(p.id),
    );
  });
  draft.status = payload.status;
};

export const selectedTab = {
  problemStatement: 'Problem Statement',
  customInput: 'Custom Input',
  results: 'Results',
  success: 'Success',
};

export const verdict = {
  compiler_error: {
    name: 'Compilation Failed',
    type: 'error',
  },
  runtime_error: {
    name: 'Runtime Error',
    type: 'error',
  },
  tle: {
    name: 'Time Limit Exceeded',
    type: 'error',
  },
  success: {
    name: 'OK',
    type: 'success',
  },
  na: {
    name: 'NA',
    type: 'info',
  },
};

const editorInitialState = {
  lang: '',
  code: {
    python3: '',
    javascript: '',
    c: '',
    cpp: '',
    java: '',
    ruby3: '',
  },
  isDirty: {
    python3: false,
    javascript: false,
    c: false,
    cpp: false,
    java: false,
    ruby3: false,
  },
  //stdin, stdout and status is for custom input testing
  stdin: '',
  stdout: '',
  verdict: '',
  //compilation/runtime error message
  stderr: '',
};

const initialState = {
  id: '',
  problems: [],
  mcqIds: [],
  sections: [],
  sectionProblems: {},
  executionList: [],
  //not really a credential, it contains invite_id, test_id and status of the invite
  InviteCredential: {},
  startTime: null,
  timerWarnLevel: 'green',
  mcqsResponses: [],
  meetingData: null,
  attendee: null,
  currentSectionId: null,
  test_settings: {},
  testInfo: {
    loading: true,
    title: '',
    instructions: '',
    totalTime: 0,
    totalProblems: 0,
    totalMcqs: 0,
  },
  surveyUrl: '',
  submittedBy: '',
  customInputLoading: false,
  assessmentSubmissionStatus: '',
};

export const problemsSelector = state => state.problems;

export const currProblemSelector = createSelector(problemsSelector, problems => {
  let currProblem = problems.filter(p => p.isCurrent);
  if (currProblem.length > 0) {
    if (currProblem[0].editor.lang === '')
      currProblem[0].editor.lang = currProblem[0].templates[0]?.language;
    return currProblem[0];
  } else {
    return {
      testcases: [],
      editor: JSON.parse(JSON.stringify(editorInitialState)),
    };
  }
});

export const activeTemplateSelector = createSelector(currProblemSelector, problem => {
  if (problem.templates) {
    let tpls = problem.templates.filter(tpl => tpl.language === problem.editor.lang);
    if (tpls.length > 0) {
      return tpls[0];
    }
  }
  return { header: '', tailer: '' };
});

export const languageListSelector = createSelector(currProblemSelector, problem => {
  if (problem.templates) {
    return problem.templates.map(t => t.language);
  }
  return [];
});

export const activeTabSelector = createSelector(currProblemSelector, problem => {
  return problem.activeTab;
});

export const attemptedCountSelector = createSelector(problemsSelector, problems => {
  return problems.filter(p => p.attempted).length;
});

export const assessmentEndTimeSelector = createSelector(
  a => a,
  a => {
    return moment.utc(a.startTime).add(a.duration, 'minutes');
  },
);

const AssessmentReducer = handleActions(
  {
    START_TEST_SUCCESS: (state, action) =>
      produce(state, draft => {
        processAssessmentData(draft, action.payload);
      }),

    ADD_TO_EXECUTION_LIST: (state, action) => {
      return {
        ...state,
        executionList: [...state.executionList, action.payload],
      };
    },

    REMOVE_FROM_EXECUTION_LIST: (state, action) => {
      let newExecutionList = state.executionList.filter(
        ex => ex.codesubmission_id !== action.payload,
      );
      return {
        ...state,
        executionList: newExecutionList,
      };
    },

    UPDATE_ASSESSMENT: (state, action) =>
      produce(state, draft => {
        processAssessmentData(draft, action.payload);
      }),

    SET_TIMER_EXP_LEVEL: (state, action) => {
      return {
        ...state,
        timerWarnLevel: action.payload.level,
      };
    },

    CHOOSE_OPTION: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.id === action.payload.problem_id) {
            p.activeTab = action.payload.activeTab;
          }
        });
      }),

    MARK_ATTEMPTED: (state, action) =>
      produce(state, draft => {
        // Update main problems array
        draft.problems.forEach(p => {
          if (p.id === action.payload.problem_id) {
            p.attempted = true;
          }
        });

        // Also update sectionProblems to keep them synchronized
        Object.keys(draft.sectionProblems).forEach(sectionId => {
          draft.sectionProblems[sectionId].forEach(p => {
            if (p.id === action.payload.problem_id) {
              p.attempted = true;
            }
          });
        });
      }),

    ASSIGN_TESTCASES: (state, action) => {
      return {
        ...state,
        testcases: action.payload,
      };
    },

    ASSIGN_LOGO: (state, action) =>
      produce(state, draft => {
        // Update main problems array
        draft.problems.forEach(p => {
          if (p.id === action.payload.id) {
            p.testcases.forEach(tc => {
              if (action.payload.logo[tc.id]) tc.logo = action.payload.logo[tc.id];
            });
            if (action.payload.status !== '') {
              p.status = action.payload.status;
            }
          }
        });

        // Also update sectionProblems to keep them synchronized
        Object.keys(draft.sectionProblems).forEach(sectionId => {
          draft.sectionProblems[sectionId].forEach(p => {
            if (p.id === action.payload.id) {
              p.testcases.forEach(tc => {
                if (action.payload.logo[tc.id]) tc.logo = action.payload.logo[tc.id];
              });
              if (action.payload.status !== '') {
                p.status = action.payload.status;
              }
            }
          });
        });
      }),

    ASSIGN_TESTCASE_DATA: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.id === action.payload.id) {
            p.testcases.forEach(tc => {
              if (action.payload.tc_executionData[tc.id]) {
                tc.compiler_error = action.payload.tc_executionData[tc.id]['compiler_error'];
                tc.runtime_error = action.payload.tc_executionData[tc.id]['runtime_error'];
                tc.stderr = action.payload.tc_executionData[tc.id]['stderr'];
                tc.timelimitexceeded_error =
                  action.payload.tc_executionData[tc.id]['timelimitexceeded_error'];
              }
            });
          }
        });
      }),

    UPDATE_TESTCASE_DATA: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.id === action.payload.id) {
            p.testcases.forEach(tc => {
              if (tc.id === action.payload.tcExecutionData['id']) {
                tc.compiler_error = action.payload.tcExecutionData['compiler_error'];
                tc.runtime_error = action.payload.tcExecutionData['runtime_error'];
                tc.stderr = action.payload.tcExecutionData['stderr'];
                tc.timelimitexceeded_error =
                  action.payload.tcExecutionData['timelimitexceeded_error'];
                tc.logo = action.payload.tcExecutionData['logo'];
              }
            });
          }
        });
      }),

    UPDATE_STATUS: (state, action) =>
      produce(state, draft => {
        // Update main problems array
        draft.problems.forEach(p => {
          if (p.id === action.payload.id) {
            if (action.payload.status !== '') {
              p.status = action.payload.status;
            }
          }
        });

        // Also update sectionProblems to keep them synchronized
        Object.keys(draft.sectionProblems).forEach(sectionId => {
          draft.sectionProblems[sectionId].forEach(p => {
            if (p.id === action.payload.id) {
              if (action.payload.status !== '') {
                p.status = action.payload.status;
              }
            }
          });
        });
      }),

    RESET_TC_RES_STATUS: (state, action) =>
      produce(state, draft => {
        draft.problems
          .filter(p => p.isCurrent)
          .forEach(p => {
            p.testcases.forEach(tc => (tc.logo = action.payload.logo));
          });
      }),

    SET_CUSTOM_INP_RESULTS: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.isCurrent) {
            p.stdout = action.payload.stdout;
            p.stderr = action.payload.stderr;
            if (action.payload.compiler_error) {
              p.verdict = verdict.compiler_error;
            } else if (action.payload.runtime_error) {
              p.verdict = verdict.runtime_error;
            } else if (action.payload.timelimitexceeded_error) {
              p.verdict = verdict.tle;
            } else {
              p.verdict = verdict.success;
            }
          }
        });
      }),

    RESET_CUSTOM_INP_RESULTS: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.isCurrent) {
            p.stdout = '';
            p.stderr = '';
            p.verdict = verdict.na;
          }
        });
      }),

    ASSIGN_PROBLEM: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.id === action.payload.problem_id) {
            p.isCurrent = true;
            if (!p.editor) {
              //perform a deep copy of the state
              p.editor = JSON.parse(JSON.stringify(editorInitialState));
            }
            let lcs = {};
            if (p.lcs) {
              p.lcs.forEach(l => {
                lcs[l.lang] = l.code;
              });
            }
            p.templates.forEach(tpl => {
              if (!p.editor.isDirty[tpl.language]) {
                let body = tpl.body;
                if (p.lcs) {
                  if (tpl.language in lcs) {
                    body = lcs[tpl.language];
                  }
                }
                p.editor.code[tpl.language] = body;
              }
            });
          } else {
            p.isCurrent = false;
          }
        });

        // Also update sectionProblems to keep them synchronized
        Object.keys(draft.sectionProblems).forEach(sectionId => {
          draft.sectionProblems[sectionId].forEach(p => {
            if (p.id === action.payload.problem_id) {
              p.isCurrent = true;
            } else {
              p.isCurrent = false;
            }
          });
        });
      }),

    EDITOR_UPDATE_CODE: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.id === action.payload.problem_id) {
            p.editor.code[p.editor.lang] = action.payload.code;
            p.editor.isDirty[p.editor.lang] = true;
          }
        });
      }),

    EDITOR_RESET_ALL: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.id === action.payload.problem_id) {
            p.editor = Object.assign({}, editorInitialState);
          }
        });
      }),

    EDITOR_ASSIGN_TEMPLATE: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.id === action.payload.problem_id) {
            p.editor.code[action.payload.lang] =
              action.payload.header + '\n\n' + action.payload.body + '\n\n' + action.payload.tailer;
          }
        });
      }),

    EDITOR_SET_INPUT: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.id === action.payload.problem_id) {
            p.editor.stdin = action.payload.stdin;
          }
        });
      }),

    EDITOR_SET_STDOUT: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.id === action.payload.problem_id) {
            p.editor.stdout = action.payload.stdout;
          }
        });
      }),

    EDITOR_UPDATE_LANG: (state, action) =>
      produce(state, draft => {
        draft.problems.forEach(p => {
          if (p.id === action.payload.problem_id) {
            p.editor.lang = action.payload.language;
          }
        });
      }),

    SET_MCQ_RESPONSES: (state, action) =>
      produce(state, draft => {
        draft.mcqsResponses = action.payload;
      }),
    CREATE_MEETING: (state, action) => {
      return {
        ...state,
        meetingData: action.payload,
      };
    },
    GET_MEETING: (state, action) => {
      return {
        ...state,
        meetingData: action.payload,
      };
    },
    GET_ATTENDEE: (state, action) => {
      return {
        ...state,
        attendee: action.payload,
      };
    },
    GET_TEST_SETTINGS: (state, action) => {
      return {
        ...state,
        test_settings: action.payload,
      };
    },
    GET_TEST_INFO: (state, action) => {
      return {
        ...state,
        testInfo: action.payload,
      };
    },
    START_RECORDING: (state, action) => {
      return {
        ...state,
      };
    },
    SET_CURRENT_SECTION_ID: (state, action) => {
      return {
        ...state,
        currentSectionId: action.payload,
      };
    },

    UPDATE_SECTION_INFO: (state, action) =>
      produce(state, draft => {
        draft.currentSectionId = action.payload.currentSectionId;
        draft.sections = action.payload.sections;
      }),

    UPDATE_OVERALL_TIMER: (state, action) =>
      produce(state, draft => {
        draft.remainingTime = action.payload;
      }),

    UPDATE_SECTION_TIMER: (state, action) =>
      produce(state, draft => {
        const section = draft.sections.find(s => s.id === draft.currentSectionId);
        if (section) {
          section.remaining_time = action.payload.remainingTime;
        }
      }),

    UPDATE_SECTION_STATUS: (state, action) =>
      produce(state, draft => {
        const sectionIndex = draft.sections.findIndex(
          section => section.id === action.payload.sectionId,
        );
        if (sectionIndex !== -1) {
          draft.sections[sectionIndex].status = action.payload.status;
        }
      }),

    GET_SURVEY_URL_SUCCESS: (state, action) => ({ ...state, surveyUrl: action.payload }),
    SET_SUBMITTED_BY: (state, action) => ({ ...state, submittedBy: action.payload }),
    SET_CUSTOM_INPUT_LOADING: (state, action) => ({ ...state, customInputLoading: true }),
    CLEAR_CUSTOM_INPUT_LOADING: (state, action) => ({ ...state, customInputLoading: false }),
    SET_ASSESSMENT_STATUS: (state, action) => ({
      ...state,
      assessmentSubmissionStatus: action.payload,
    }),
  },
  initialState,
);

export default AssessmentReducer;
